import { z } from 'zod';

import { IsoDateTimeSchema } from './common.js';

/**
 * Public SEO plumbing for the website: where a URL whose content was taken
 * down should send its visitors, and the list of URLs that are live right now.
 * Both are read straight off content status, so there is nothing to keep in
 * sync - deactivate an item and it redirects and leaves the sitemap; bring it
 * back and both undo themselves.
 */

/** Answer of `GET /website/seo/gone?path=` when the path used to be content. */
export const WebsiteGoneRedirectSchema = z.object({
  /** Site-relative path to send the visitor to. Always starts with `/`. */
  to: z.string().startsWith('/'),
  /**
   * `true` when the content is deleted / archived and will not come back
   * (301-class redirect). `false` when it is only switched off - a draft or an
   * inactive package can return, and browsers cache a permanent redirect
   * forever, so that case must stay temporary (302-class).
   */
  permanent: z.boolean(),
});
export type WebsiteGoneRedirect = z.infer<typeof WebsiteGoneRedirectSchema>;

export const WebsiteSitemapEntrySchema = z.object({
  /** Site-relative path, e.g. `/blogs/best-time-to-visit-bali`. */
  path: z.string().startsWith('/'),
  lastModified: IsoDateTimeSchema,
});
export type WebsiteSitemapEntry = z.infer<typeof WebsiteSitemapEntrySchema>;

export const WebsiteSitemapResponseSchema = z.object({
  urls: z.array(WebsiteSitemapEntrySchema),
});
export type WebsiteSitemapResponse = z.infer<typeof WebsiteSitemapResponseSchema>;
